import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { pmService, type PayerPriority } from "../../services/pm.service";
import { Select } from "../ui/Select";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const THRESHOLD = 5;
const CURRENT_YEAR = new Date().getFullYear();

const FIRST_YEAR = 2015;

const PERIOD_OPTIONS: { value: string; label: string }[] = [
  { value: "ytd", label: "Year to Date" },
  ...Array.from({ length: CURRENT_YEAR - FIRST_YEAR + 1 }, (_, i) => {
    const y = CURRENT_YEAR - i;
    return { value: String(y), label: String(y) };
  }),
];

interface FilterState {
  period: string;
  priority: PayerPriority;
}

const defaultFilter = (): FilterState => ({ period: "ytd", priority: "primary" });

export const DenialKPIWidget: React.FC = () => {
  const [pending, setPending] = useState<FilterState>(defaultFilter);
  const [applied, setApplied] = useState<FilterState>(defaultFilter);

  const hasChanges = pending.period !== applied.period || pending.priority !== applied.priority;

  const { data, isLoading } = useQuery({
    queryKey: ["pm-denial-kpi", applied.period, applied.priority],
    queryFn: () => {
      const ytd = applied.period === "ytd";
      const year = ytd ? CURRENT_YEAR : parseInt(applied.period, 10);
      return pmService.getDenialKPI(year, ytd, applied.priority);
    },
  });

  const months = data?.months ?? [];
  const categories = months.map((m) => MONTHS[m.month - 1]);
  const values = months.map((m) => m.denialRate);
  const maxValue = values.length > 0 ? Math.max(...values) : 0;
  const yAxisMax = Math.ceil(Math.max(maxValue * 1.25, THRESHOLD + 2));

  const overallDenied = months.reduce((s, m) => s + m.deniedClaims, 0);
  const overallTotal = months.reduce((s, m) => s + m.totalClaims, 0);
  const overallRate =
    overallTotal > 0 ? parseFloat(((overallDenied / overallTotal) * 100).toFixed(2)) : 0;

  const chartOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      animations: { enabled: false },
    },
    plotOptions: {
      bar: {
        borderRadius: 2,
        columnWidth: "55%",
        dataLabels: { position: "top" },
        colors: {
          ranges: [
            { from: 0, to: THRESHOLD - 0.001, color: "#699CBB" },
            { from: THRESHOLD, to: 100, color: "#e05252" },
          ],
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => (val > 0 ? `${val}%` : ""),
      style: { fontSize: "11px", colors: ["#374151"], fontWeight: "600" },
      offsetY: -22,
    },
    annotations: {
      yaxis: [
        {
          y: THRESHOLD,
          borderColor: "#e05252",
          borderWidth: 2,
          strokeDashArray: 5,
          label: {
            text: `${THRESHOLD}% threshold`,
            position: "right",
            borderColor: "transparent",
            style: {
              background: "transparent",
              color: "#e05252",
              fontSize: "11px",
              fontWeight: "600",
            },
          },
        },
      ],
    },
    xaxis: {
      categories,
      labels: { style: { fontSize: "12px", colors: "#6b7280" } },
      axisBorder: { show: true, color: "#BCBDC5" },
      axisTicks: { show: false },
    },
    yaxis: {
      min: 0,
      max: yAxisMax,
      labels: {
        formatter: (val: number) => `${val}%`,
        style: { fontSize: "11px", colors: "#6b7280" },
      },
    },
    tooltip: {
      y: {
        formatter: (val: number, opts) => {
          const m = months[opts.dataPointIndex];
          if (!m) return `${val}%`;
          return `${val}% (${m.deniedClaims.toLocaleString()} / ${m.totalClaims.toLocaleString()} claims)`;
        },
      },
    },
    grid: {
      strokeDashArray: 3,
      borderColor: "#e5e7eb",
      padding: { right: 80 },
    },
    legend: { show: false },
  };

  return (
    <div className="bg-white border-2 border-border-gray">
      {/* Header */}
      <div className="bg-primary-blue text-white px-4 md:px-6 py-3">
        <h3 className="font-bold text-lg">Denial KPI Scorecard</h3>
        <span className="text-xs sm:text-sm">Denial rate trends over time</span>
      </div>

      {/* Controls */}
      <div className="p-4 bg-gray-50 border-b-2 border-border-gray flex flex-wrap gap-3 items-end">
        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-700 mb-1">Period</label>
          <Select
            value={pending.period}
            onChange={(e) => setPending((prev) => ({ ...prev, period: e.target.value }))}
          >
            {PERIOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-700 mb-1">Payor</label>
          <Select
            value={pending.priority}
            onChange={(e) =>
              setPending((prev) => ({ ...prev, priority: e.target.value as PayerPriority }))
            }
          >
            <option value="all">All</option>
            <option value="primary">Primary</option>
            <option value="secondary">Secondary</option>
            <option value="primary_secondary">Primary+Secondary</option>
            <option value="tertiary">Tertiary</option>
          </Select>
        </div>

        <button
          type="button"
          onClick={() => setApplied(pending)}
          disabled={!hasChanges}
          className="h-10 px-6 bg-primary-blue text-white text-xs sm:text-sm hover:bg-indigo-darker-blue disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap flex-shrink-0 transition-colors"
        >
          Apply
        </button>
      </div>

      {/* Chart */}
      <div className="px-4 pb-4 pt-2">
        {isLoading && <div className="text-center py-12 text-gray-500">Loading KPI data...</div>}

        {!isLoading && months.length === 0 && (
          <div className="text-center py-12 text-gray-500">No data for the selected period.</div>
        )}

        {!isLoading && months.length > 0 && (
          <>
            {/* Summary stats */}
            <div className="flex gap-6 mb-2 pt-2 text-xs text-gray-500 uppercase tracking-wide font-bold">
              <span>
                Overall:{" "}
                <span
                  className={`font-bold text-sm ${overallRate >= THRESHOLD ? "text-red-500" : "text-gray-800"}`}
                >
                  {overallRate}%
                </span>
              </span>
              <span>
                Total claims:{" "}
                <span className="font-bold text-sm text-gray-800">
                  {overallTotal.toLocaleString()}
                </span>
              </span>
              <span>
                Total denied:{" "}
                <span className="font-bold text-sm text-gray-800">
                  {overallDenied.toLocaleString()}
                </span>
              </span>
            </div>

            <ReactApexChart
              options={chartOptions}
              series={[{ name: "Denial Rate", data: values }]}
              type="bar"
              height={320}
            />
          </>
        )}
      </div>
    </div>
  );
};
